import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash-es';
import { Field } from 'react-final-form';
import { useRedirectSimulation } from 'hooks';
import { useQueryParams } from 'hooks/useQueryParams';
import {
  simulateAddPaymentMethodToCustomer,
  simulateCompletePayment
} from 'redux/actions/3ds';
import {
  get3DSSimulateData,
  get3DSSimulateLoadingError,
  get3DSSimulateLoadingSuccess
} from 'redux/selectors/3ds';
import {
  ERROR_CREDENTIALS_PAYMENT,
  ERROR_CREDENTIALS_REDIRECT,
  SUCCESS_CREDENTIALS_PAYMENT,
  SUCCESS_CREDENTIALS_REDIRECT
} from 'consts/3ds';
import { ERROR_MESSAGES } from 'consts/tokenization';
import Layout from 'components/layout/Layout';
import { HeaderCompContent } from 'components/layout/Header';
import Typography from 'components/core/Typography/Typography';
import { Typographies, ThemeFamilies, getThemeTypography } from 'models';
import Button, { ButtonVariants } from 'components/core/Button/Button';
import BoxWrapper from 'components/layout/BoxWrapper';
import Input from 'components/formElements/Input/Input';
import BaseReactFinalForm from 'components/formElements/BaseReactFinalForm';
import StyledFormWrapper from 'components/layout/StyledFormWrapper';
import { fieldsWrapperStyle } from 'components/layout/styles';
import { CtaButtonTextStyle } from 'styles/global';

export enum SimulatorPageType {
  BankPayment = 'BankPayment',
  BankRedirect = 'BankRedirect'
}

const SimulatorTypeConsts = {
  [SimulatorPageType.BankPayment]: {
    title: '3ds_simulator.bank_payment_title',
    description: '3ds_simulator.bank_payment_description',
    labelParam1: 'username',
    labelParam2: 'password',
    cta: 'pay',
    success: SUCCESS_CREDENTIALS_PAYMENT,
    error: ERROR_CREDENTIALS_PAYMENT,
    onSubmit: simulateCompletePayment
  },
  [SimulatorPageType.BankRedirect]: {
    title: '3ds_simulator.bank_tokenization_title',
    description: '3ds_simulator.bank_tokenization_description',
    labelParam1: 'iban_name',
    labelParam2: 'otp_name',
    cta: 'submit',
    success: SUCCESS_CREDENTIALS_REDIRECT,
    error: ERROR_CREDENTIALS_REDIRECT,
    onSubmit: simulateAddPaymentMethodToCustomer
  }
};

enum SimulatorFormFieldsNames {
  param1 = 'param1',
  param2 = 'param2',
  activeAction = 'activeAction'
}

interface IFormValues {
  [SimulatorFormFieldsNames.param1]: string;
  [SimulatorFormFieldsNames.param2]: string;
  [SimulatorFormFieldsNames.activeAction]: 'success' | 'error';
}

interface IProps {
  type: SimulatorPageType;
}

const CompleteBankSimulator = ({ type }: IProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const simulateResponse = useSelector(get3DSSimulateData);
  const simulateErrorLoading = useSelector(get3DSSimulateLoadingError);
  const simulateSuccessLoading = useSelector(get3DSSimulateLoadingSuccess);
  const simulateLoading = simulateErrorLoading || simulateSuccessLoading;
  const inputDisabled = simulateLoading || !isEmpty(simulateResponse);

  const {
    token,
    complete_payment_url: completeUrlParam,
    error_payment_url: errorUrlParam
  } = useQueryParams() || {};

  useRedirectSimulation({
    paymentToken: token,
    simulateResponse,
    type,
    completeUrlParam,
    errorUrlParam
  });

  const onSubmit = (values: IFormValues) => {
    const params = {
      token,
      param1: values[SimulatorFormFieldsNames.param1],
      param2: values[SimulatorFormFieldsNames.param2]
    };
    dispatch(SimulatorTypeConsts[type].onSubmit(params));
  };

  const validateForm = (values: IFormValues) => {
    const errors = {} as Record<keyof IFormValues, any>;
    const requiredFieldError = 'required_field_error';
    const invalidFieldError = ERROR_MESSAGES.invalid_field;
    const param1 = values[SimulatorFormFieldsNames.param1];
    const param2 = values[SimulatorFormFieldsNames.param2];
    if (!param1) {
      errors[SimulatorFormFieldsNames.param1] = requiredFieldError;
    } else {
      const possibleParam1Values = [
        SimulatorTypeConsts[type].success.param1,
        SimulatorTypeConsts[type].error.param1
      ];
      if (!possibleParam1Values.includes(param1)) {
        errors[SimulatorFormFieldsNames.param1] = invalidFieldError;
      }
    }
    if (!param2) {
      errors[SimulatorFormFieldsNames.param2] = requiredFieldError;
    } else {
      const possibleParam2Values = [
        SimulatorTypeConsts[type].success.param2,
        SimulatorTypeConsts[type].error.param2
      ];
      if (!possibleParam2Values.includes(param2)) {
        errors[SimulatorFormFieldsNames.param2] = invalidFieldError;
      }
    }
    return errors;
  };

  return (
    <StyledLayout
      headerProps={{
        withBack: false,
        withLogo: false,
        noTranslations: true
      }}
      renderHeader={false}
    >
      <BoxWrapper>
        <SimulatorTitle type={Typographies.H5_HEADING}>
          {t(SimulatorTypeConsts[type].title)}
        </SimulatorTitle>
        <SimulatorSubtitle type={Typographies.BODY1}>
          {t(SimulatorTypeConsts[type].description)}
        </SimulatorSubtitle>
        <FormWrapper>
          <BaseReactFinalForm
            onSubmit={onSubmit}
            validate={validateForm}
            render={({ form, values, handleSubmit }) => {
              return (
                <StyledFormWrapper onSubmit={handleSubmit}>
                  <Actions>
                    <SuccessAction
                      isLoading={simulateSuccessLoading}
                      disabled={inputDisabled}
                      themeFamily={ThemeFamilies.SUCCESS}
                      variant={
                        values[SimulatorFormFieldsNames.activeAction] === 'success'
                          ? ButtonVariants.FILLED
                          : ButtonVariants.OUTLINED
                      }
                      onClick={e => {
                        e.preventDefault();
                        form.change(SimulatorFormFieldsNames.activeAction, 'success');
                        form.change(
                          SimulatorFormFieldsNames.param1,
                          SimulatorTypeConsts[type].success.param1
                        );
                        form.change(
                          SimulatorFormFieldsNames.param2,
                          SimulatorTypeConsts[type].success.param2
                        );
                      }}
                    >
                      {t('success')}
                    </SuccessAction>
                    <FailAction
                      isLoading={simulateErrorLoading}
                      disabled={inputDisabled}
                      themeFamily={ThemeFamilies.ERROR}
                      variant={
                        values[SimulatorFormFieldsNames.activeAction] === 'error'
                          ? ButtonVariants.FILLED
                          : ButtonVariants.OUTLINED
                      }
                      onClick={e => {
                        e.preventDefault();
                        form.change(SimulatorFormFieldsNames.activeAction, 'error');
                        form.change(
                          SimulatorFormFieldsNames.param1,
                          SimulatorTypeConsts[type].error.param1
                        );
                        form.change(
                          SimulatorFormFieldsNames.param2,
                          SimulatorTypeConsts[type].error.param2
                        );
                      }}
                    >
                      {t('error')}
                    </FailAction>
                  </Actions>
                  <InputWrapper>
                    <Field
                      name={SimulatorFormFieldsNames.param1}
                      component={Input}
                      fieldBoxProps={{
                        label: t(SimulatorTypeConsts[type].labelParam1)
                      }}
                      disabled={inputDisabled}
                    />
                    <Field
                      name={SimulatorFormFieldsNames.param2}
                      component={Input}
                      fieldBoxProps={{
                        label: t(SimulatorTypeConsts[type].labelParam2)
                      }}
                      disabled={inputDisabled}
                    />
                  </InputWrapper>
                  <StyledButton
                    isLoading={simulateLoading}
                    data-testid="place_order_button"
                    disabled={inputDisabled}
                    type="submit"
                  >
                    {t(SimulatorTypeConsts[type].cta)}
                  </StyledButton>
                </StyledFormWrapper>
              );
            }}
          />
        </FormWrapper>
      </BoxWrapper>
    </StyledLayout>
  );
};

export default CompleteBankSimulator;

const StyledLayout = styled(Layout)`
  margin-top: 4rem;
  ${HeaderCompContent} {
    border: unset;
  }
`;

const SimulatorTitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.secondaryText};
  font-weight: 500;
`;

const SimulatorSubtitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.surface1};
  margin-top: 0.9rem;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const FormWrapper = styled.div`
  margin-top: 4.5rem;
`;

const SuccessAction = styled(Button)`
  flex: 1;
  border-radius: 0.8rem;
  padding: 1.8rem 4rem;
  margin-right: 2.4rem;

  > * {
    ${getThemeTypography(Typographies.H7_HEADING)}
  }
`;

const FailAction = styled(Button)`
  flex: 1;
  border-radius: 0.8rem;
  padding: 1.8rem 4rem;

  > * {
    ${getThemeTypography(Typographies.H7_HEADING)}
  }
`;

const InputWrapper = styled.div`
  ${fieldsWrapperStyle};
  margin-top: 4rem;
`;

const StyledButton = styled(Button)`
  background: ${({ theme }) => theme.colors.link.primary};
  border-radius: 0.8rem;
  width: 100%;
  padding: 1.8rem 4rem;
  margin-top: 2rem;

  > * {
    ${CtaButtonTextStyle};
  }
`;
